import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { documentsApi, llmApi, templatesApi } from '@/lib/api'
import type { VariantRecord } from '@/lib/api'
import { cockpitKeys } from '../useCockpit'
import { DELIVERED, armOptions, parseArmValue, type ArmOption, type ArmRequest } from './armOptions'
import { buildRecordRows, isRecordDocument, matchesQuery, type RecordColumn, type RecordRow } from './recordRows'

/**
 * The Datensätze tab's own controller.
 *
 * Deliberately **not** shared with the Dokumente tab's list: that one shows a
 * filtered page of documents with a selection and a bulk bar, this one walks
 * every analysed document to build a table whose columns come from the
 * template. The two disagree about page size, about what a row is and about
 * when a row disappears; unifying them would mean a controller that serves
 * neither. What they do share is the cache prefix, so
 * `cockpit.invalidate.documents()` still refreshes this tab.
 */

/** 50 at a time, as the Flutter records tab does. A document's `result` map
 *  is large — one entry per box with coordinates and reviews — so the 1000
 *  the gateway would allow is a multi-megabyte response for one screen. */
export const RECORDS_PAGE_SIZE = 50

export interface RecordsState {
  columns: RecordColumn[]
  rows: RecordRow[]
  /** Rows before the search box narrowed them, for "3 von 50". */
  loadedCount: number
  /** Analysed documents on the server, from the shared counts — a server
   *  figure, unlike the loaded-row count. Undefined while counts load. */
  totalRecords?: number
  needsReviewTotal?: number
  hasMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  error: unknown
  loadMore: () => void
  refetch: () => void

  needsReviewOnly: boolean
  setNeedsReviewOnly: (value: boolean) => void
  query: string
  setQuery: (value: string) => void

  /** The selected option's key (`DELIVERED` or `arm|model`). */
  arm: string
  setArm: (value: string) => void
  armOptions: ArmOption[]
  /** What the selected key means for a request — the arm and, where the
   *  reading has one, its model. Null while the delivered values are shown. */
  armRequest: ArmRequest | null
  /** True while an arm is selected whose records the gateway returned none of
   *  — see `armCoverage`. */
  armEmpty: boolean
  /** How many of the loaded rows actually show the selected arm's reading. */
  armCoverage: { shown: number; total: number }
  isArmLoading: boolean
  armError: unknown
}

/**
 * How many analysed documents the template has.
 *
 * Derived from the shared counts rather than from the loaded rows, because
 * the header must not count up as you page. `complete` and `problematic` are
 * the two states a document reaches by being analysed; `neu`, `inProgress`
 * and `noMatch` carry no values and are not records. It is a derivation and
 * not a server figure for "records" — there is no such endpoint — so the
 * screen says "Dokumente mit Ergebnis" where it needs to be exact.
 */
export function analysedCount(byStatus?: Partial<Record<string, number>>): number {
  return (byStatus?.complete ?? 0) + (byStatus?.problematic ?? 0)
}

export function useRecords(
  templateId: string,
  options: { threshold: number; counts?: { byStatus?: Record<string, number>; needsReview?: number } },
): RecordsState {
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false)
  const [query, setQuery] = useState('')
  const [arm, setArm] = useState<string>(DELIVERED)

  // The columns. Owned by this tab; the layout loads the template but not its
  // field set, and the field set is what a column is.
  const fieldsQuery = useQuery({
    queryKey: ['templateFields', templateId],
    queryFn: () => templatesApi.fields(templateId),
    enabled: templateId.length > 0,
    staleTime: 5 * 60_000,
  })

  // `needs_review=true` is a real server filter (`wantsOnlyFlagged`), so the
  // "Nur unklare" tab narrows the request rather than hiding loaded rows —
  // which matters at 50 per page, where a client-side filter would show four
  // of the template's twelve flagged documents and look complete.
  const documentsQuery = useInfiniteQuery({
    queryKey: cockpitKeys.documents(templateId, { tab: 'records', needsReviewOnly }),
    queryFn: ({ pageParam }) =>
      documentsApi.list(templateId, {
        page: pageParam,
        pageSize: RECORDS_PAGE_SIZE,
        ...(needsReviewOnly ? { needsReview: true } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.nextCursor ? last.page + 1 : undefined),
    enabled: templateId.length > 0,
    staleTime: 10_000,
  })

  // Which readings exist at all. Without this the picker would offer arms
  // nobody ever ran and every one of them would render an unchanged table.
  const armsQuery = useQuery({
    queryKey: ['arms', templateId],
    queryFn: () => llmApi.arms(templateId),
    enabled: templateId.length > 0,
    staleTime: 60_000,
    retry: false,
  })

  // The selected key carries the model, because a variant is keyed
  // `(document, arm, model)` and two models on one arm are two readings.
  const armRequest: ArmRequest | null = useMemo(() => parseArmValue(arm), [arm])

  const variantsQuery = useQuery({
    queryKey: ['variantRecords', templateId, armRequest?.arm, armRequest?.model ?? ''],
    queryFn: () =>
      documentsApi.variantRecords(templateId, armRequest!.arm, {
        ...(armRequest!.model ? { model: armRequest!.model } : {}),
      }),
    enabled: templateId.length > 0 && armRequest !== null,
    staleTime: 60_000,
  })

  const columns = useMemo(
    () => [...(fieldsQuery.data ?? [])].sort((a, b) => a.ordinal - b.ordinal),
    [fieldsQuery.data],
  )

  const docs = useMemo(
    () => (documentsQuery.data?.pages ?? []).flatMap((page) => page.docs),
    [documentsQuery.data],
  )

  const variants = useMemo(() => {
    if (arm === DELIVERED) return undefined
    const map = new Map<string, VariantRecord>()
    for (const record of variantsQuery.data ?? []) map.set(record.documentId, record)
    return map
  }, [arm, variantsQuery.data])

  const allRows = useMemo(
    () => buildRecordRows(docs, columns, options.threshold, variants),
    [docs, columns, options.threshold, variants],
  )

  const rows = useMemo(
    () => allRows.filter((row) => matchesQuery(row, query)),
    [allRows, query],
  )

  const armCoverage = useMemo(
    () => ({
      shown: allRows.filter((row) => !row.fallbackToDelivered).length,
      total: allRows.length,
    }),
    [allRows],
  )

  return {
    columns,
    rows,
    loadedCount: allRows.length,
    totalRecords: options.counts ? analysedCount(options.counts.byStatus) : undefined,
    needsReviewTotal: options.counts?.needsReview,
    hasMore: documentsQuery.hasNextPage,
    isLoading: fieldsQuery.isLoading || documentsQuery.isLoading,
    isLoadingMore: documentsQuery.isFetchingNextPage,
    error: fieldsQuery.error ?? documentsQuery.error,
    loadMore: () => void documentsQuery.fetchNextPage(),
    refetch: () => {
      void fieldsQuery.refetch()
      void documentsQuery.refetch()
      if (arm !== DELIVERED) void variantsQuery.refetch()
    },
    needsReviewOnly,
    setNeedsReviewOnly,
    query,
    setQuery,
    arm,
    setArm,
    armOptions: armOptions(armsQuery.data),
    armRequest,
    armEmpty:
      arm !== DELIVERED &&
      !variantsQuery.isLoading &&
      (variantsQuery.data?.length ?? 0) === 0 &&
      docs.some(isRecordDocument),
    armCoverage,
    isArmLoading: variantsQuery.isLoading && arm !== DELIVERED,
    armError: variantsQuery.error,
  }
}
